// Catálogo — Vista DJ / Crate. Nuevo shell (mobile-first):
//  - Arranca con su título semántico ("Catálogo" + subtítulo descriptivo).
//  - Buscador ágil con micro-icono de forma de onda.
//  - Contador + botón de filtros compacto: "25 DE 1.348 VINILOS" · "FILTROS (2)".
//  - "Crate cards" depuradas: carátula limpia (sin número superpuesto), título y
//    artista, sello en ámbar cálido, formato/edición, año·país en línea propia,
//    género·subgénero, y panel inferior con nº de catálogo, badges Goldmine y
//    acciones de pre-escucha y guardado.
//  - Paginación ergonómica al final de la lista.
// Toda la lógica de búsqueda/API/permisos se mantiene intacta.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { friendlyError } from '../api/friendlyError.js';
import { usePullToRefresh } from '../ui/usePullToRefresh.js';
import type { SearchCriteria } from '../api/endpoints.js';
import type { Record, RecordSyncStatus, SearchResult } from '../types.js';
import { AdvancedSearchForm } from './AdvancedSearchForm.js';
import {
  ButtonSpinner,
  EmptyState,
  ErrorNotice,
  GoldmineBadge,
  Icon,
  PageHeader,
  SkeletonList,
  Spinner,
} from '../ui/components.js';

const PAGE_SIZE = 25;

/** Campos de orden ofrecidos al usuario (Req. 3.10). Las etiquetas se traducen. */
const SORT_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'internal_cat_num', labelKey: 'catalog.sort.internalCatNum' },
  { value: '', labelKey: 'catalog.sort.recent' },
  { value: 'artist', labelKey: 'catalog.sort.artist' },
  { value: 'title', labelKey: 'catalog.sort.title' },
  { value: 'year', labelKey: 'catalog.sort.year' },
  { value: 'genre', labelKey: 'catalog.sort.genre' },
  { value: 'commercial_value', labelKey: 'catalog.sort.value' },
];

/**
 * Contexto del catálogo que se preserva al navegar al detalle de un disco y se
 * restaura al volver ("Volver a los resultados"): búsqueda, filtros, orden,
 * página y posición de scroll. Se guarda en `sessionStorage` (vive lo que dure
 * la pestaña) para que el catálogo no se reinicie al remontarse.
 */
interface CatalogContextSnapshot {
  mode: 'quick' | 'advanced' | 'photo';
  q: string;
  criteria: SearchCriteria;
  sort: string;
  dir: 'asc' | 'desc';
  page: number;
  scrollY: number;
}

const CATALOG_STATE_KEY = 'vinilos.catalog.context';

function loadCatalogSnapshot(): CatalogContextSnapshot | null {
  try {
    const raw = sessionStorage.getItem(CATALOG_STATE_KEY);
    if (!raw) return null;
    // Consumo ÚNICO: el snapshot solo sirve para restaurar "Volver a los
    // resultados" tras entrar al detalle de un disco (se re-guarda justo antes
    // de navegar al detalle). Se BORRA al leerlo para que una carga inicial
    // posterior (reabrir la PWA, volver al Catálogo por otra vía) arranque
    // SIN filtros/búsqueda previos — en particular, la búsqueda avanzada no
    // debe traer filtros activos por defecto.
    sessionStorage.removeItem(CATALOG_STATE_KEY);
    const s = JSON.parse(raw) as Partial<CatalogContextSnapshot>;
    // El modo Foto no se restaura (la imagen/OCR no persisten); cae a rápida.
    const mode = s.mode === 'advanced' ? 'advanced' : 'quick';
    return {
      mode,
      q: typeof s.q === 'string' ? s.q : '',
      criteria: s.criteria && typeof s.criteria === 'object' ? s.criteria : {},
      sort: typeof s.sort === 'string' ? s.sort : 'internal_cat_num',
      dir: s.dir === 'desc' ? 'desc' : 'asc',
      page: typeof s.page === 'number' && s.page >= 1 ? s.page : 1,
      scrollY: typeof s.scrollY === 'number' && s.scrollY >= 0 ? s.scrollY : 0,
    };
  } catch {
    return null;
  }
}

function saveCatalogSnapshot(snapshot: CatalogContextSnapshot): void {
  try {
    sessionStorage.setItem(CATALOG_STATE_KEY, JSON.stringify(snapshot));
  } catch {
    // sessionStorage no disponible (modo privado, cuota): se ignora.
  }
}

export function CatalogView() {
  const { t } = useTranslation();
  const api = useApi();
  // Snapshot preservado (si se volvió del detalle): restaura búsqueda/orden/página.
  // Se lee una sola vez al montar (lazy init de los useState de abajo).
  const restored = useRef<CatalogContextSnapshot | null>(loadCatalogSnapshot());
  const [mode, setMode] = useState<'quick' | 'advanced' | 'photo'>(
    restored.current?.mode ?? 'quick',
  );
  const [q, setQ] = useState(restored.current?.q ?? '');
  const [criteria, setCriteria] = useState<SearchCriteria>(restored.current?.criteria ?? {});
  // Estado del modo Foto (búsqueda por OCR contra el catálogo local).
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [ocrText, setOcrText] = useState<string | null>(null);
  // Orden por defecto: por ID interno de disco (posición física) al abrir la
  // sección y en cada lista de resultados filtrados.
  const [sort, setSort] = useState(restored.current?.sort ?? 'internal_cat_num');
  const [dir, setDir] = useState<'asc' | 'desc'>(restored.current?.dir ?? 'asc');
  const [page, setPage] = useState(restored.current?.page ?? 1);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSort, setShowSort] = useState(false);

  const runSearch = useCallback(
    async (nextPage = page) => {
      const opts = { sort: sort || undefined, dir, page: nextPage, page_size: PAGE_SIZE };
      // Modo Foto: sin imagen seleccionada no hay nada que buscar todavía.
      if (mode === 'photo' && !photoFile) return;
      setLoading(true);
      setError(null);
      try {
        let res;
        if (mode === 'quick') {
          res = await api.records.quickSearch(q, opts);
        } else if (mode === 'advanced') {
          res = await api.records.advancedSearch(criteria, opts);
        } else {
          // Foto: sube la imagen, OCR en el backend y busca en el catálogo local.
          const file = photoFile as File;
          const { upload_url, s3_key } = await api.ingestion.createUpload(
            file.type || 'image/jpeg',
            file.name,
          );
          await fetch(upload_url, {
            method: 'PUT',
            headers: { 'content-type': file.type || 'image/jpeg' },
            body: file,
          });
          const photoRes = await api.records.searchByPhoto(s3_key, opts);
          setOcrText(photoRes.ocr_text ?? '');
          res = photoRes;
        }
        setResult(res);
        setPage(nextPage);
      } catch (err) {
        setError(friendlyError(err, t, 'catalog.searchError'));
      } finally {
        setLoading(false);
      }
    },
    [api, mode, q, criteria, sort, dir, page, photoFile],
  );

  const totalPages = useMemo(
    () => (result ? Math.max(1, Math.ceil(result.total / result.page_size)) : 1),
    [result],
  );

  // Referencia a la última versión de `runSearch` para invocarla desde efectos
  // sin re-disparar por su cambio de identidad (depende de `page`, etc.).
  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  // Pull-to-refresh: tirar hacia abajo en el tope recarga la búsqueda vigente
  // (misma página). Mejora progresiva (solo táctil); no interfiere en escritorio.
  const { pull, refreshing, threshold } = usePullToRefresh(() => runSearchRef.current(page), {
    disabled: loading,
  });

  // Carga inicial. Si se volvió del detalle hay un snapshot: se recarga la MISMA
  // página guardada (los filtros/orden ya se restauraron en los useState) y, tras
  // pintar los resultados, se restaura la posición de scroll. Si no hay snapshot,
  // se muestra la colección por defecto (página 1, orden por ID interno).
  useEffect(() => {
    const snap = restored.current;
    if (snap) {
      // Consumimos el snapshot una vez para no re-restaurar en futuros montajes
      // dentro de la misma instancia.
      restored.current = null;
      const targetScroll = snap.scrollY;
      void runSearchRef.current(snap.page).then(() => {
        // Espera a que el DOM pinte la lista antes de restaurar el scroll.
        requestAnimationFrame(() => window.scrollTo(0, targetScroll));
      });
    } else {
      void runSearchRef.current(1);
    }
    // Efecto de montaje (una sola vez): usa `runSearchRef` (ref estable) a
    // propósito para restaurar scroll / lanzar la búsqueda inicial.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aplicar el ordenamiento automáticamente al cambiar campo o dirección: se
  // re-lanza la búsqueda vigente (rápida o avanzada) desde la página 1. Se omite
  // el primer render porque la carga inicial ya trae el orden por defecto.
  const sortHydrated = useRef(false);
  useEffect(() => {
    if (!sortHydrated.current) {
      sortHydrated.current = true;
      return;
    }
    void runSearchRef.current(1);
    // Reacciona solo a cambios de orden (`sort`/`dir`) y usa `runSearchRef`
    // (ref estable) a propósito; no debe depender de la función de búsqueda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, dir]);

  const shown = result?.items.length ?? 0;
  const total = result?.total ?? 0;

  // Nº de criterios de búsqueda activos (para la píldora de ordenamiento y el
  // botón de reinicio). El orden NO cuenta como filtro: es una opción aparte.
  const activeSearchCount = useMemo(() => {
    if (mode === 'advanced') {
      return Object.values(criteria).filter((v) => v !== undefined && v !== null && v !== '')
        .length;
    }
    if (mode === 'photo') return photoFile ? 1 : 0;
    return q.trim() ? 1 : 0;
  }, [mode, criteria, q, photoFile]);

  // Reinicia la búsqueda vigente (rápida/avanzada/foto) y recarga el catálogo
  // por defecto. No toca el modo ni el ordenamiento seleccionado.
  const resetSearch = useCallback(() => {
    if (mode === 'advanced') {
      setCriteria({});
      void runSearchRef.current(1);
    } else if (mode === 'photo') {
      setPhotoFile(null);
      setPhotoPreview(null);
      setOcrText(null);
      // El modo Foto no auto-carga; al reiniciar mostramos el catálogo por
      // defecto con una búsqueda rápida vacía.
      void (async () => {
        setLoading(true);
        setError(null);
        try {
          const res = await api.records.quickSearch('', {
            sort: sort || undefined,
            dir,
            page: 1,
            page_size: PAGE_SIZE,
          });
          setResult(res);
          setPage(1);
        } catch (err) {
          setError(friendlyError(err, t, 'catalog.searchError'));
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setQ('');
      void runSearchRef.current(1);
    }
  }, [mode, api, sort, dir]);

  // Guarda el contexto vigente + el scroll actual antes de navegar al detalle,
  // para que "Volver a los resultados" restaure exactamente este punto. El modo
  // Foto no es restaurable (imagen/OCR no persisten): se guarda como 'quick'.
  const persistContext = useCallback(() => {
    saveCatalogSnapshot({
      mode: mode === 'photo' ? 'quick' : mode,
      q,
      criteria,
      sort,
      dir,
      page,
      scrollY: window.scrollY,
    });
  }, [mode, q, criteria, sort, dir, page]);

  return (
    <div className="flex flex-col">
      {/* Indicador de pull-to-refresh: aparece al tirar hacia abajo en el tope. */}
      {(pull > 0 || refreshing) && (
        <div
          className="flex items-center justify-center overflow-hidden text-on-surface-variant"
          style={{ height: refreshing ? 40 : pull }}
          aria-hidden
        >
          <Icon
            name={refreshing ? 'autorenew' : 'arrow_downward'}
            className={`text-[22px] ${
              refreshing
                ? 'animate-spin text-primary'
                : pull >= threshold
                  ? 'text-primary rotate-180 transition-transform'
                  : 'transition-transform'
            }`}
          />
        </div>
      )}
      {/* La exportación del catálogo se removió de aquí: ya está disponible
          desde el Menú (Gestión de Datos → Exportar a Excel). */}
      <PageHeader
        eyebrow={t('catalog.eyebrow')}
        title={t('catalog.title')}
        subtitle={t('catalog.subtitle')}
      />

      {/* Buscador + controles */}
      <div className="px-margin-mobile pt-space-md pb-space-md flex flex-col gap-space-sm">
        {/* Toggle modo búsqueda: Rápida · Avanzada · Foto */}
        <div
          data-tour="catalog-search-modes"
          className="flex items-center gap-space-xs p-1 rounded-lg bg-surface-deck border border-border-subtle"
        >
          <button
            type="button"
            onClick={() => setMode('quick')}
            className={`flex-1 h-10 rounded flex items-center justify-center gap-space-xs font-body-sm text-body-sm transition-colors ${
              mode === 'quick' ? 'bg-surface-rack text-primary' : 'text-on-surface-variant'
            }`}
          >
            <Icon name="bolt" className="text-[16px]" /> {t('catalog.modeQuick')}
          </button>
          <button
            type="button"
            onClick={() => setMode('advanced')}
            className={`flex-1 h-10 rounded flex items-center justify-center gap-space-xs font-body-sm text-body-sm transition-colors ${
              mode === 'advanced' ? 'bg-surface-rack text-primary' : 'text-on-surface-variant'
            }`}
          >
            <Icon name="tune" className="text-[16px]" /> {t('catalog.modeAdvanced')}
          </button>
          <button
            type="button"
            onClick={() => setMode('photo')}
            className={`flex-1 h-10 rounded flex items-center justify-center gap-space-xs font-body-sm text-body-sm transition-colors ${
              mode === 'photo' ? 'bg-surface-rack text-primary' : 'text-on-surface-variant'
            }`}
          >
            <Icon name="photo_camera" className="text-[16px]" /> {t('catalog.modePhoto')}
          </button>
        </div>

        {/* Contenedor del panel de búsqueda (básica o avanzada) para
            diferenciarlo visualmente del resto de la vista. */}
        <div className="rounded-xl border border-border-subtle bg-surface-deck p-space-md">
          {/* El modo avanzado trae su propia cabecera plegable; para los otros
              modos mantenemos el encabezado simple del contenedor. */}
          {mode !== 'advanced' && (
            <div className="flex items-center gap-space-xs mb-space-sm">
              <Icon
                name={mode === 'quick' ? 'bolt' : 'photo_camera'}
                className="text-primary text-[16px]"
              />
              <span className="section-label text-on-surface-variant">
                {mode === 'quick' ? t('catalog.quickSearch') : t('catalog.photoSearch')}
              </span>
            </div>
          )}
          {mode === 'quick' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void runSearch(1);
              }}
            >
              <div className="field-slot h-12">
                <Icon name="graphic_eq" className="text-primary text-[20px] mr-space-xs" />
                <input
                  className="field-input"
                  placeholder={t('catalog.quickPlaceholder')}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                {q && (
                  <button
                    type="button"
                    aria-label={t('catalog.clearSearch')}
                    onClick={resetSearch}
                    className="w-9 h-9 shrink-0 rounded text-on-surface-variant flex items-center justify-center hover:text-on-surface"
                  >
                    <Icon name="close" className="text-[20px]" />
                  </button>
                )}
                <button
                  type="submit"
                  aria-label={t('catalog.search')}
                  className="w-9 h-9 shrink-0 rounded bg-primary-container/20 text-primary flex items-center justify-center"
                >
                  <Icon name="search" className="text-[20px]" />
                </button>
              </div>
            </form>
          ) : mode === 'advanced' ? (
            <AdvancedSearchForm
              criteria={criteria}
              onChange={setCriteria}
              onSearch={() => void runSearch(1)}
              onClear={resetSearch}
              title={t('catalog.advancedSearch')}
              icon="tune"
            />
          ) : (
            <PhotoSearchPanel
              file={photoFile}
              preview={photoPreview}
              ocrText={ocrText}
              busy={loading}
              onPick={(f) => {
                setPhotoFile(f);
                setPhotoPreview(f ? URL.createObjectURL(f) : null);
                setOcrText(null);
              }}
              onSearch={() => void runSearch(1)}
              onClear={resetSearch}
            />
          )}
        </div>

        {/* Contador + reinicio + botón de ordenamiento compacto */}
        <div className="flex items-center justify-between gap-space-sm">
          <div className="flex items-center gap-space-xs px-3 h-9 rounded-lg bg-surface-rack border border-primary-container/40 text-primary">
            <Icon name="album" className="text-[16px]" />
            <span className="font-code-catalog text-body-sm uppercase tracking-wide">
              {t('catalog.countVinyls', { shown, total: total ? total.toLocaleString() : '—' })}
            </span>
          </div>
          <div className="flex items-center gap-space-xs">
            {activeSearchCount > 0 && (
              <button
                type="button"
                onClick={resetSearch}
                className="chip h-9"
                aria-label={t('catalog.resetSearch')}
              >
                <Icon name="restart_alt" className="text-[16px]" /> {t('catalog.reset')}
              </button>
            )}
            <button
              type="button"
              data-tour="catalog-sort"
              onClick={() => setShowSort((v) => !v)}
              className={`chip h-9 ${showSort ? '!text-primary !border-primary-container/60' : ''}`}
            >
              <Icon name="sort" className="text-[16px]" /> {t('catalog.sorting')}
              <Icon
                name="expand_more"
                className={`text-[16px] transition-transform ${showSort ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        </div>

        {/* Ordenamiento (se aplica al seleccionar) */}
        {showSort && (
          <div className="surface-card flex flex-col gap-space-sm">
            <div className="flex flex-col gap-space-xs">
              <label className="field-label">{t('catalog.sortBy')}</label>
              <div className="field-slot">
                <Icon name="sort" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <select
                  className="field-input"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-space-xs">
              <label className="field-label">{t('catalog.direction')}</label>
              <div className="field-slot">
                <Icon
                  name="swap_vert"
                  className="text-on-surface-variant text-[18px] mr-space-xs"
                />
                <select
                  className="field-input"
                  value={dir}
                  onChange={(e) => setDir(e.target.value as 'asc' | 'desc')}
                >
                  <option value="asc">{t('catalog.asc')}</option>
                  <option value="desc">{t('catalog.desc')}</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resultados */}
      <div className="px-margin-mobile pb-space-lg flex flex-col gap-space-sm">
        {error && <ErrorNotice message={error} onRetry={() => void runSearch(page)} />}
        {/* Primer loading (aún sin resultados): skeletons con la forma de las
            tarjetas, que se perciben más rápidos que un spinner. En recargas
            (ya hay resultados) mantenemos un spinner ligero. */}
        {loading && (result ? <Spinner label={t('catalog.searchingCrate')} /> : <SkeletonList />)}

        {result && !loading && (
          <>
            <div className="flex items-center">
              <span className="section-label text-on-surface-variant">
                {t('catalog.order')}:{' '}
                <span className="text-primary">
                  {t(SORT_OPTIONS.find((o) => o.value === sort)?.labelKey ?? 'catalog.sort.recent')}
                </span>
              </span>
            </div>

            {result.items.length === 0 ? (
              // Distinguimos dos vacíos: (a) catálogo realmente vacío (primer
              // uso: sin filtros activos y total 0) → estado con coaching y CTA
              // "Agregar con foto"; (b) búsqueda/filtros sin coincidencias → el
              // mensaje clásico "sin coincidencias".
              activeSearchCount === 0 && total === 0 ? (
                <EmptyState
                  icon="album"
                  title={t('catalog.emptyFirstRunTitle')}
                  message={t('catalog.emptyFirstRunBody')}
                  action={
                    <Link to="/add" className="btn-cyan">
                      <Icon name="photo_camera" className="text-[18px]" />{' '}
                      {t('catalog.emptyFirstRunCta')}
                    </Link>
                  }
                />
              ) : (
                <div className="surface-card text-center text-on-surface-variant font-body-sm text-body-sm py-space-lg">
                  {t('catalog.noMatches')}
                </div>
              )
            ) : (
              result.items.map((r) => (
                <RecordCard key={r.id} record={r} onNavigate={persistContext} />
              ))
            )}

            {totalPages > 1 && (
              <div className="flex items-center gap-space-sm mt-space-md">
                <button
                  className="btn-ghost flex-1"
                  disabled={page <= 1}
                  onClick={() => void runSearch(page - 1)}
                >
                  <Icon name="arrow_back" className="text-[18px]" /> {t('catalog.previous')}
                </button>
                <span className="font-code-catalog text-body-sm text-on-surface-variant whitespace-nowrap">
                  {t('catalog.page')} <span className="text-on-surface font-bold">{page}</span> /{' '}
                  {totalPages}
                </span>
                <button
                  className="btn-secondary flex-1"
                  disabled={page >= totalPages}
                  onClick={() => void runSearch(page + 1)}
                >
                  {t('catalog.next')} <Icon name="arrow_forward" className="text-[18px]" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Formato + edición. `editionKey` es una clave i18n ('reissue'|'original'). */
function formatLine(r: Record): {
  format: string;
  editionKey: 'catalog.reissue' | 'catalog.original';
} {
  const discs = r.discs_per_album > 1 ? `${r.discs_per_album}x` : '';
  const size = r.size_inches ? ` ${r.size_inches}"` : '';
  const format = `${discs}${r.type}${size}`.trim();
  const isReissue =
    r.original_release_year != null &&
    r.edition_year != null &&
    r.original_release_year !== r.edition_year;
  return { format, editionKey: isReissue ? 'catalog.reissue' : 'catalog.original' };
}

/**
 * Carátula del disco: muestra la imagen (CloudFront) cuando existe y cae al
 * icono de álbum si no hay portada o la imagen falla al cargar. Mantiene el
 * mismo marco cuadrado en ambos estados.
 */
function CoverImage({ url, title }: { url?: string | null; title: string }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(url) && !failed;
  return (
    // La carátula se estira a todo el alto de la fila (`self-stretch`) para
    // igualar el bloque de texto de la derecha; mantiene proporción cuadrada con
    // un ancho mínimo, así se ve más grande sin desbalancear la tarjeta.
    <div className="self-stretch w-28 shrink-0 rounded-lg bg-surface-container-high border border-border-subtle overflow-hidden flex items-center justify-center">
      {showImage ? (
        <img
          src={url as string}
          alt={t('catalog.coverAlt', { title })}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <Icon name="album" className="text-on-surface-variant text-[40px] opacity-50" />
      )}
    </div>
  );
}

/**
 * Metadatos visuales por estado de sincronización con Discogs (Req. 10.10).
 * Cuatro estados: pendiente, sincronizado, omitido (procesado sin coincidencia o
 * ya al día) y fallado (el proceso falló).
 */
function syncStatusMeta(
  t: TFunction,
): globalThis.Record<
  RecordSyncStatus,
  { label: string; icon: string; className: string; tooltip: string }
> {
  return {
    synced: {
      label: t('catalog.sync.synced'),
      icon: 'cloud_done',
      className: 'bg-goldmine-mint/15 border-goldmine-mint/40 text-goldmine-mint',
      tooltip: t('catalog.sync.syncedTooltip'),
    },
    pending: {
      label: t('catalog.sync.pending'),
      icon: 'cloud_off',
      className: 'bg-surface-container border-border-subtle text-on-surface-variant',
      tooltip: t('catalog.sync.pendingTooltip'),
    },
    skipped: {
      label: t('catalog.sync.skipped'),
      icon: 'do_not_disturb_on',
      className: 'bg-surface-container border-border-subtle text-on-surface-variant',
      tooltip: t('catalog.sync.skippedTooltip'),
    },
    failed: {
      label: t('catalog.sync.failed'),
      icon: 'error',
      className: 'bg-error/15 border-error/40 text-error',
      tooltip: t('catalog.sync.failedTooltip'),
    },
  };
}

/**
 * Estado de sincronización con Discogs del disco. Usa la columna real
 * `sync_status`; si no viniera (datos antiguos), se deriva de `last_synced_at`
 * (sincronizado si tiene fecha, pendiente si no).
 */
function SyncStatusBadge({ record: r }: { record: Record }) {
  const { t } = useTranslation();
  const status: RecordSyncStatus = r.sync_status ?? (r.last_synced_at ? 'synced' : 'pending');
  const meta = syncStatusMeta(t)[status];
  const tooltip =
    status === 'synced' && r.last_synced_at
      ? `${meta.tooltip} · ${formatDate(r.last_synced_at)}`
      : meta.tooltip;
  return (
    <span
      title={tooltip}
      className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-badge-micro text-badge-micro uppercase tracking-wide border ${meta.className}`}
    >
      <Icon name={meta.icon} className="text-[13px]" />
      {meta.label}
    </span>
  );
}

/** Fecha corta legible (es). Reutilizable en tarjetas y badges. */
function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('es');
}

/**
 * Panel del modo Foto (Req. 3.9): permite tomar/subir una imagen; al buscar, la
 * imagen se sube, se procesa con OCR en el backend y el texto detectado se usa
 * para buscar coincidencias en el catálogo local. Muestra el texto OCR (si lo
 * hay) para transparencia.
 */
function PhotoSearchPanel({
  file,
  preview,
  ocrText,
  busy,
  onPick,
  onSearch,
  onClear,
}: {
  file: File | null;
  preview: string | null;
  ocrText: string | null;
  busy: boolean;
  onPick: (f: File | null) => void;
  onSearch: () => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-space-sm">
      <p className="font-body-sm text-body-sm text-on-surface-variant">{t('catalog.photo.hint')}</p>

      {preview ? (
        <div className="relative h-40 rounded-xl border border-border-subtle bg-surface-container-lowest/40 overflow-hidden">
          <img
            src={preview}
            alt={t('catalog.photo.previewAlt')}
            className="w-full h-full object-contain"
          />
          {/* Acciones sobre la imagen: reemplazar (elige otra) o eliminar. */}
          <div className="absolute top-space-xs right-space-xs flex items-center gap-space-xs">
            <label
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-surface-deck/90 backdrop-blur border border-border-subtle text-on-surface font-body-sm text-body-sm cursor-pointer active:scale-95 transition-transform"
              aria-label={t('catalog.photo.replaceAria')}
            >
              <Icon name="swap_horiz" className="text-[16px]" /> {t('catalog.photo.replace')}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={busy}
                onChange={(e) => onPick(e.target.files?.[0] ?? null)}
              />
            </label>
            <button
              type="button"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-surface-deck/90 backdrop-blur border border-error/40 text-error font-body-sm text-body-sm active:scale-95 transition-transform disabled:opacity-50"
              disabled={busy}
              onClick={() => onPick(null)}
              aria-label={t('catalog.photo.deleteAria')}
            >
              <Icon name="delete" className="text-[16px]" /> {t('catalog.photo.delete')}
            </button>
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center gap-space-xs h-40 rounded-xl border border-dashed border-border-subtle bg-surface-container-lowest/40 cursor-pointer overflow-hidden">
          <Icon name="add_a_photo" className="text-on-surface-variant text-[32px] opacity-60" />
          <span className="font-body-sm text-body-sm text-on-surface-variant">
            {t('catalog.photo.pick')}
          </span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
        </label>
      )}

      {ocrText !== null && (
        <div className="surface-card !p-space-sm">
          <span className="section-label text-vacuum-glow">{t('catalog.photo.detectedText')}</span>
          <p className="font-body-sm text-body-sm text-on-surface mt-1 whitespace-pre-wrap break-words">
            {ocrText.trim() === '' ? t('catalog.photo.noText') : ocrText}
          </p>
        </div>
      )}

      <div className="flex items-center gap-space-sm">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={!file || busy}
          onClick={onSearch}
        >
          {busy ? <ButtonSpinner /> : <Icon name="search" className="text-[18px]" />}{' '}
          {busy ? t('catalog.photo.processing') : t('catalog.photo.searchByPhoto')}
        </button>
        {file && (
          <button type="button" className="btn-secondary" onClick={onClear} disabled={busy}>
            {t('catalog.photo.clear')}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Crate card depurada: carátula limpia + datos jerárquicos y panel inferior con
 * nº de catálogo, badges Goldmine (M / S) y acciones de pre-escucha y guardado.
 */
function RecordCard({ record: r, onNavigate }: { record: Record; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const genre = r.genre_name ?? r.discogs_genre ?? '—';
  const sub = r.subgenre ?? r.discogs_styles ?? null;
  // Sello discográfico destacado (fallback: artista o formato base).
  const label = r.label_name ?? null;
  const artist = r.artist_name ?? null;
  const { format, editionKey } = formatLine(r);

  return (
    <Link
      to={`/records/${r.id}`}
      onClick={onNavigate}
      className="relative flex flex-col rounded-xl bg-surface-deck border border-border-subtle overflow-hidden active:scale-[0.99] transition-transform"
    >
      {/* Franja lateral cian (lomo de vinilo) */}
      <span className="absolute left-0 top-0 bottom-0 w-1 bg-primary-container/70" />
      <div className="flex gap-space-sm p-space-sm pl-space-md pr-space-md">
        {/* Cover limpia (sin número superpuesto) */}
        <CoverImage url={r.cover_image_url} title={r.title} />
        {/* Datos */}
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          {/* Título (amarillo). El estado de sync se muestra en el footer para no
              pisar el título cuando es largo. `break-words` + `[overflow-wrap:anywhere]`
              para que incluso un token larguísimo sin espacios (p. ej. un nº de
              catálogo pegado) se parta y NO se salga de la tarjeta en móvil. */}
          <h3 className="font-headline-sm text-headline-sm text-vacuum-glow break-words [overflow-wrap:anywhere]">
            {r.title}
          </h3>
          {/* Artista: tamaño mayor para darle jerarquía. */}
          <p className="font-headline-sm text-headline-sm text-on-surface break-words [overflow-wrap:anywhere]">
            {artist ?? t('catalog.noArtist')}
          </p>

          {/* Sello discográfico en blanco normal. `min-w-0` para que `truncate`
              funcione dentro del contenedor flex (si no, el texto fuerza el ancho
              y se desborda en móvil). */}
          {label && (
            <p className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest truncate min-w-0">
              {label}
            </p>
          )}

          {/* Formato y edición */}
          <div className="flex items-center gap-space-xs mt-0.5">
            <span className="font-code-catalog text-body-sm text-on-surface">{format}</span>
            <span className="px-1.5 py-0.5 rounded bg-surface-container text-on-surface-variant font-badge-micro text-badge-micro uppercase">
              {t(editionKey)}
            </span>
          </div>

          {/* Año y país (línea independiente) */}
          <div className="flex items-center gap-space-xs text-on-surface-variant font-body-sm text-body-sm">
            <Icon name="calendar_today" className="text-[13px]" />
            <span>
              {r.edition_year ?? '—'}
              {r.origin_country ? ` · ${r.origin_country}` : ''}
            </span>
          </div>

          {/* Género (línea propia). `items-start` + `min-w-0` para que el texto
              largo se parta debajo del icono sin empujar el ancho de la tarjeta. */}
          <div className="flex items-start gap-space-xs text-on-surface-variant font-body-sm text-body-sm min-w-0">
            <Icon name="music_note" className="text-[13px] text-primary shrink-0 mt-0.5" />
            <span className="text-on-surface break-words [overflow-wrap:anywhere] min-w-0">
              {genre}
            </span>
          </div>

          {/* Subgénero en LÍNEA SEPARADA (puede ser largo) */}
          {sub && (
            <div className="flex items-start gap-space-xs text-on-surface-variant font-body-sm text-body-sm min-w-0">
              <Icon
                name="subdirectory_arrow_right"
                className="text-[13px] text-primary/70 shrink-0 mt-0.5"
              />
              <span className="break-words [overflow-wrap:anywhere] min-w-0">{sub}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer: secciones separadas — ID · Cat# · Media · Carátula — + estado sync.
          En móvil estrecho la fila ENVUELVE (`flex-wrap`) en lugar de desbordarse:
          antes era una fila rígida de 4 columnas + badge que se salía de la tarjeta
          en pantallas pequeñas. El grupo de metadatos puede encogerse (`min-w-0`) y
          el badge de sync se va a su propia línea si no cabe. */}
      <div className="flex flex-wrap items-center justify-between gap-x-space-sm gap-y-space-xs px-space-md py-space-sm border-t border-border-subtle bg-surface-container-lowest/40">
        <div className="flex flex-wrap items-center gap-x-space-sm gap-y-space-xs min-w-0">
          {/* ID interno del disco (número de posición física). Primero, a la
              izquierda del Nº de catálogo. */}
          <div className="flex flex-col gap-0.5 min-w-0 max-w-[8rem]">
            <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wide">
              {t('catalog.idLabel')}
            </span>
            <span className="font-code-catalog text-badge-micro text-primary truncate">
              {r.internal_cat_num ?? '—'}
            </span>
          </div>
          {/* Nº de catálogo */}
          <div className="flex flex-col gap-0.5 min-w-0 max-w-[10rem] border-l border-border-subtle pl-space-sm">
            <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wide">
              {t('catalog.catLabel')}
            </span>
            <span className="font-code-catalog text-badge-micro text-on-surface truncate">
              {r.label_catalog_nr ?? '—'}
            </span>
          </div>
          {/* Estado del disco (Media) */}
          <div className="flex flex-col items-center gap-0.5 shrink-0 border-l border-border-subtle pl-space-sm">
            <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wide">
              {t('catalog.mediaLabel')}
            </span>
            {r.media_condition ? (
              <GoldmineBadge grade={r.media_condition} />
            ) : (
              <span className="font-badge-micro text-badge-micro text-on-surface-variant">—</span>
            )}
          </div>
          {/* Estado de la carátula (Sleeve) */}
          <div className="flex flex-col items-center gap-0.5 shrink-0 border-l border-border-subtle pl-space-sm">
            <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wide">
              {t('catalog.sleeveLabel')}
            </span>
            {r.sleeve_condition ? (
              <GoldmineBadge grade={r.sleeve_condition} />
            ) : (
              <span className="font-badge-micro text-badge-micro text-on-surface-variant">—</span>
            )}
          </div>
        </div>
        {/* Estado de sincronización con Discogs (aquí no interfiere con el título). */}
        <div className="shrink-0">
          <SyncStatusBadge record={r} />
        </div>
      </div>
    </Link>
  );
}
